import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { NgbModal, type NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { filter, map, mergeMap, tap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
  expectedConsumptionDefinedAction,
  SupplyType,
  usageQuestionnaireCloseModalAction,
  UsageQuestionnaireModalComponent,
} from '@innogy/common-ui/sales';
import { ofFormSubmitAction } from '@innogy/utils-deprecated';
import { MarkAsUnsubmittedAction, SetValueAction } from 'ngrx-forms';
import {
  getEstimatedConsumptionByHouseTypeSuccess,
  getEstimatedConsumptionByHouseType,
  type HouseType,
  type Residents,
} from '@essent/estimate-consumption';

import {
  homeBatteryWizardConsumptionQuestionnaireCloseModalAction,
  homeBatteryWizardConsumptionQuestionnaireOpenModalAction,
  homeBatteryWizardFormSubmitAction,
} from './homebattery-wizard.actions';
import { selectHomeBatteryFunnelSettings } from '../funnel/homebattery-funnel.selectors';
import { selectHomeBatteryConsumptionState } from './homebattery-wizard.selectors';
import { HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID } from './homebattery-wizard.reducer';
import { navigateToQuestionairePageAction } from '../funnel/homebattery-funnel.actions';

@Injectable()
export class HomeBatteryWizardEffects {
  public readonly store$ = inject(Store);
  private readonly modalService = inject(NgbModal);
  private readonly actions$ = inject(Actions);

  funnelSettings$ = this.store$.select(selectHomeBatteryFunnelSettings);

  private consumptionEstimateModal?: NgbModalRef;

  public readonly openConsumptionEstimateModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(homeBatteryWizardConsumptionQuestionnaireOpenModalAction),
        concatLatestFrom(() => this.funnelSettings$),
        tap(([_, funnelSettings]) => {
          this.consumptionEstimateModal = this.modalService.open(
            UsageQuestionnaireModalComponent,
            {
              centered: true,
              keyboard: true,
            }
          );

          this.consumptionEstimateModal.componentInstance.formState$ =
            this.store$.select(selectHomeBatteryConsumptionState);
          this.consumptionEstimateModal.componentInstance.rendering =
            funnelSettings.usageWizard;
        })
      ),
    { dispatch: false }
  );

  public readonly closeConsumptionEstimateModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(homeBatteryWizardConsumptionQuestionnaireCloseModalAction),
        tap(() => {
          this.consumptionEstimateModal?.close();
        })
      ),
    { dispatch: false }
  );

  public readonly applyConsumptionEstimateToHomebatteryWizardForm$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(expectedConsumptionDefinedAction),
        filter(
          (action) => action.formId === HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID
        ),
        mergeMap((action) => {
          return [
            new SetValueAction(
              'solarPanelsWizardFormConsumptionControlId',
              action.expectedConsumption.electricity.total
            ),
            homeBatteryWizardConsumptionQuestionnaireCloseModalAction(),
          ];
        })
      )
    );

  private readonly usageQuestionnaireFormValues$ = this.store$.pipe(
    select(selectHomeBatteryConsumptionState)
  );

  public readonly submitUsageQuestionnaire$ = createEffect(() => {
    return this.actions$.pipe(
      ofFormSubmitAction(HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID),
      concatLatestFrom(() => [this.usageQuestionnaireFormValues$]),
      filter(
        ([_, questionnaireState]) =>
          questionnaireState.isValid && questionnaireState.isSubmitted
      ),
      mergeMap(([_, questionnaireState]) => [
        getEstimatedConsumptionByHouseType({
          payload: {
            residents: questionnaireState.value.residents as Residents,
            houseType: questionnaireState.value.houseType as HouseType,
          },
          actionId: HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID,
        }),
        usageQuestionnaireCloseModalAction(),
      ])
    );
  });

  public readonly markUsageQuestionnaireAsUnsubmitted$ = createEffect(() =>
    this.actions$.pipe(
      ofFormSubmitAction(HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID),
      map(
        () =>
          new MarkAsUnsubmittedAction(HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID)
      )
    )
  );

  public readonly handleEstimatedConsumptionByHouseTypeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getEstimatedConsumptionByHouseTypeSuccess),
        filter(
          (action) =>
            action.actionId === HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID
        ),
        map(({ payload: expectedConsumption }) =>
          expectedConsumptionDefinedAction({
            formId: HOMEBATTERY_CONSUMPTION_QUESTIONNAIRE_ID,
            expectedConsumption,
            supplyType: SupplyType.E_ONLY,
          })
        )
      )
  );

  public readonly handleHomeBatteryWizardSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(homeBatteryWizardFormSubmitAction),
      concatLatestFrom(() => this.funnelSettings$),
      map(([_, funnelSettings]) => {
        return navigateToQuestionairePageAction({
          pageData: funnelSettings.questionnairePage,
        });
      })
    )
  );
}
